import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  IMPORTABLE_EXCEL_FORMAT_LIST,
  SHOP_USE_FILE_NAME,
  SHOP_USE_SHEET_NAME,
} from '../../../_constants/manageConst';

import { useTranslation } from 'react-i18next';
import FileImportModal from '../../../_common/Modal/FileImportModal';
import { PartNoList } from '../../../services/apiTypes';
import XLSX from 'xlsx';
import React from 'react';
import { FileDownloadOutlined, FileUploadOutlined } from '@mui/icons-material';
import { getShopUse, updateShopUse } from '../../../services/api/shopUse';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import useErrorModal from '../../../hooks/useErrorModal';
import { useSelector } from 'react-redux';
import { isAdmin } from '../../../_utils/utils';
import { RootState } from '../../../stores/store';
import { LOADING_BACKDROP_STYLE } from '../../../_constants/styleConst';
import { isPartNoList } from '../../../_utils/typeGuardUtils';
import {
  convertInputDataToCorrectType,
  createExportExcelName,
} from '../../../_utils/utils';
import { API_CODE } from '../../../_constants/apiConst';

type OrganizationImportExportProps = {
  companyKey: string;
  companyName?: string;
};

const OrganizationImportExport: React.FC<OrganizationImportExportProps> = ({
  companyKey,
  companyName,
}) => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);

  const [file, setFile] = useState<File | undefined>();
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<0 | 1 | 2 | 3>(0);
  const { error, showApiError, showTypeError, getApiErrorMessage, hideError } =
    useErrorModal();
  const [errorMsg, setErrorMsg] = useState<string>('');

  /** インポート */
  const doImportShopUse = async () => {
    setImportStatus(1);
    setErrorMsg('');

    // ファイルが未選択
    if (!file || !IMPORTABLE_EXCEL_FORMAT_LIST.includes(file.type)) {
      setImportStatus(3);
      setErrorMsg(t('ERR_IMPORT'));
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'buffer', bookVBA: true });
      const partNoSheet = workbook.Sheets[SHOP_USE_SHEET_NAME];

      // シートが無ければエラーにする
      if (partNoSheet === undefined) {
        setImportStatus(3);
        setErrorMsg(t('ERR_IMPORT'));
        return;
      }

      const partNoListExcel: PartNoList[] = JSON.parse(
        JSON.stringify(
          XLSX.utils.sheet_to_json<PartNoList>(partNoSheet, {
            raw: true,
            blankrows: true,
            header: 1,
          }),
        ),
      );

      const formattedPartNoList = partNoListExcel
        .map((data) => {
          return convertInputDataToCorrectType(data, 'string');
        })
        .filter((item) => item !== '');

      await updateShopUse({
        companyKey,
        partNoList: formattedPartNoList,
      });

      setImportStatus(2);
    } catch (error) {
      setImportStatus(3);
      setErrorMsg(getApiErrorMessage(error, API_CODE.PARTS_NO_LIST));
    }
  };

  /** エクスポート */
  const doExportShopUse = async () => {
    setIsLoading(true);
    try {
      const workbook = XLSX.utils.book_new();
      try {
        const response = await getShopUse({ companyKey });
        if (!isPartNoList(response)) {
          showTypeError(API_CODE.PARTS_NO_LIST);
        }
        const data = response.map((item) => [item.toString()]);
        const sheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, sheet, SHOP_USE_SHEET_NAME);
        const baseFileName = `${(companyName ?? companyKey).replaceAll(' ', '_')}_${SHOP_USE_FILE_NAME}`;
        XLSX.writeFile(
          workbook,
          createExportExcelName(baseFileName, userData.dateFormat),
          { compression: true },
        );
      } catch (error) {
        showApiError(error, API_CODE.PARTS_NO_LIST);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClickCancelImportModal = () => {
    setIsOpenImportModal(false);
    setFile(undefined);
    setImportStatus(0);
    setErrorMsg('');
  };

  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', lg: 'row' },
      }}
    >
      <Box py={1}>
        <Typography>{t('LBL_SHOP_USE')}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: { xs: '100%', lg: '60%' },
        }}
      >
        <Box
          sx={{
            display: isAdmin(userData) ? '' : 'none',
            width: '50%',
          }}
          minWidth={'150px'}
          mr={'1rem'}
        >
          <Button
            variant="contained"
            sx={{ bgcolor: '#1E83BE' }}
            onClick={() => setIsOpenImportModal(true)}
            fullWidth
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileDownloadOutlined sx={{ fontSize: '1.3rem' }} />
              {t('BTN_IMPORT')}
            </Box>
          </Button>
        </Box>
        <Box minWidth={'150px'} sx={{ width: '50%' }}>
          <Button
            variant="contained"
            sx={{ bgcolor: '#E0356C' }}
            onClick={doExportShopUse}
            fullWidth
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileUploadOutlined sx={{ fontSize: '1.3rem' }} />
              {t('BTN_EXPORT')}
            </Box>
          </Button>
        </Box>
      </Box>
      <FileImportModal
        isOpen={isOpenImportModal}
        handleClose={onClickCancelImportModal}
        onClickOk={doImportShopUse}
        acceptableFiles={IMPORTABLE_EXCEL_FORMAT_LIST}
        setFile={setFile}
        file={file}
        status={importStatus}
        errorMsg={errorMsg}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default OrganizationImportExport;
